import { useEffect, useState } from "react";
import { Bell, Check, Trash2, CheckCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, EmptyState, SeverityBadge } from "@/components/Logo";
import api from "@/lib/api";
import { pct, dateFr } from "@/lib/format";
import { t } from "@/i18n";

const FILTERS = [["all", t("alerts.toutes")], ["red", t("alerts.critique_15")], ["orange", t("alerts.moderee_5_15")], ["green", t("alerts.faible_5")]];
const BORDER = { red: "border-l-rose-500", orange: "border-l-amber-500", green: "border-l-emerald-500" };

export default function Alerts() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/alerts").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const act = async (fn) => { await fn(); await load(); window.dispatchEvent(new Event("alerts:changed")); };
  if (!items) return <div className="flex h-64 items-center justify-center" data-testid="alerts-loading"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const shown = items.filter((a) => filter === "all" || a.severity === filter);

  return (
    <>
      <PageHeader eyebrow={t("alerts.surveillance")} title={t("common.alertes_prix")} description={t("alerts.chaque_nouvelle_facture_est_comparee_aux_p")}
        actions={items.some((a) => !a.read) && <Button variant="outline" onClick={() => act(() => api.post("/alerts/read-all"))} data-testid="alerts-read-all-btn"><CheckCheck className="mr-2 h-4 w-4" />{t("alerts.tout_marquer_comme_lu")}</Button>} />
      <div className="mb-5 flex flex-wrap gap-2">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={k === "all" ? "filter-severity-all" : `filter-severity-${k}`}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${filter === k ? "border-foreground bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
            {l} <span className="num ml-1 opacity-70">{k === "all" ? items.length : items.filter((a) => a.severity === k).length}</span>
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <EmptyState icon={Bell} title={t("alerts.aucune_alerte")} description={t("alerts.les_hausses_de_prix_apparaitront_ici_apres")} testId="alerts-empty" />
      ) : (
        <div className="space-y-2" data-testid="alerts-table">
          {shown.map((a) => (
            <div key={a.id} data-testid={`alert-item-${a.id}`} className={`flex flex-col gap-3 rounded-xl border border-l-4 bg-card p-4 sm:flex-row sm:items-center ${BORDER[a.severity]} ${a.read ? "opacity-60" : ""}`}>
              <SeverityBadge severity={a.severity} testId={`alert-severity-${a.id}`}>{pct(a.change_pct, true)}</SeverityBadge>
              <div className="flex-1">
                <p className="font-semibold">{a.title}</p>
                <p className="text-sm text-muted-foreground">{a.message}</p>
              </div>
              <span className="text-xs text-muted-foreground">{dateFr(a.created_at)}</span>
              <div className="flex gap-1">
                {!a.read && <Button variant="ghost" size="icon" onClick={() => act(() => api.post(`/alerts/${a.id}/read`))} data-testid={`alert-read-btn-${a.id}`} aria-label={t("alerts.marquer_comme_lu")}><Check className="h-4 w-4" /></Button>}
                <Button variant="ghost" size="icon" onClick={() => act(() => api.delete(`/alerts/${a.id}`))} data-testid={`alert-delete-btn-${a.id}`} aria-label={t("common.supprimer")}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
